import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { Alert, Button, Form, List, Select } from "antd";
import { useMemo, useState } from "react";
import { TemplateMetaDataField } from "@devowl-wp/api-real-cookie-banner";
import { useAllUntranslatables } from "../../../../../../hooks/useAllUntranslatables.js";
import { useUntranslatableCreate } from "../../../../../../hooks/useUntranslatableCreate.js";
import { useUntranslatableDelete } from "../../../../../../hooks/useUntranslatableDelete.js";
import { useUntranslatables } from "../../../../../../hooks/useUntranslatables.js";
import { useTranslation } from "../../../../../../others/i18n.js";
import { useStores } from "../../../../../../store/root.js";
/**
 * Untranslatable Manager component
 * Helps to manage untranslatable texts for a template by adding and removing them.
 * Works for Service and Content-Blocker templates.
 */ const UntranslatableManager = ({ templateType, templateIdentifier })=>{
    const { t } = useTranslation();
    const { notificationStore } = useStores();
    const [selectedText, setSelectedText] = useState("");
    const form = Form.useFormInstance();
    const templateId = form.getFieldValue(TemplateMetaDataField.Id);
    const { data: untranslatablesData, isLoading, refetch } = useUntranslatables(templateType, templateIdentifier, !!templateType && !!templateIdentifier);
    const { data: allUntranslatablesData, isLoading: isLoadingAll } = useAllUntranslatables(!!templateType && !!templateIdentifier);
    const createMutation = useUntranslatableCreate();
    const deleteMutation = useUntranslatableDelete();
    const [selectKey, setSelectKey] = useState(0);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const untranslatables = useMemo(()=>{
        const items = untranslatablesData?.untranslatables || [];
        return [
            ...items
        ].sort((a, b)=>a.text.localeCompare(b.text));
    }, [
        untranslatablesData?.untranslatables
    ]);
    const allUntranslatables = useMemo(()=>{
        const items = allUntranslatablesData?.untranslatables || [];
        return [
            ...items
        ].sort((a, b)=>a.text.localeCompare(b.text));
    }, [
        allUntranslatablesData?.untranslatables
    ]);
    const availableTexts = useMemo(()=>{
        if (!templateType || !templateIdentifier) return [];
        // Get all texts that are not already linked to this template
        const linkedTexts = new Set(untranslatables.map((item)=>item.text));
        return allUntranslatables.map((item)=>item.text).filter((text)=>!linkedTexts.has(text));
    }, [
        allUntranslatables,
        untranslatables,
        templateType,
        templateIdentifier
    ]);
    const isNewText = useMemo(()=>{
        if (!selectedText?.trim()) return false;
        // Check if the text already exists in all untranslatables
        const allTexts = new Set(allUntranslatables.map((item)=>item.text));
        return !allTexts.has(selectedText.trim());
    }, [
        selectedText,
        allUntranslatables
    ]);
    const handleAddOrSelect = async (text)=>{
        if (!text?.trim()) return;
        try {
            await createMutation.mutateAsync({
                text: text.trim(),
                template: {
                    type: templateType,
                    identifier: templateIdentifier
                }
            });
            notificationStore.add({
                type: "success",
                message: t("Untranslatable added successfully"),
                duration: 2,
                placement: "top"
            });
            setSelectedText(""); // Clear the input field
            setSelectKey((prev)=>prev + 1); // Force re-render of select
            refetch();
        } catch (error) {
            notificationStore.addFromServerError(error, t);
        }
    };
    const handleDelete = async (text)=>{
        try {
            await deleteMutation.mutateAsync({
                text,
                template: {
                    type: templateType,
                    identifier: templateIdentifier
                }
            });
            notificationStore.add({
                type: "success",
                message: t("Untranslatable removed successfully"),
                duration: 2,
                placement: "top"
            });
            refetch();
        } catch (error) {
            notificationStore.addFromServerError(error, t);
        }
    };
    // Check if template has an id - if not, show notice instead of functionality
    if (!templateId) {
        return /*#__PURE__*/ _jsx("div", {
            children: /*#__PURE__*/ _jsx(Alert, {
                description: t("Feature is only available for already created templates. Please save the template first to manage untranslatable texts."),
                type: "info",
                showIcon: true
            })
        });
    }
    return /*#__PURE__*/ _jsxs("div", {
        children: [
            /*#__PURE__*/ _jsx("div", {
                style: {
                    marginBottom: 16
                },
                children: /*#__PURE__*/ _jsx(Select, {
                    showSearch: true,
                    allowClear: true,
                    placeholder: t("Search or add untranslatable text..."),
                    onSelect: handleAddOrSelect,
                    onSearch: (value)=>{
                        setSelectedText(value);
                        setSelectedIndex(availableTexts.findIndex((text)=>text.includes(value)));
                    },
                    onKeyDown: (event)=>{
                        if (event.key === "Enter" && isNewText && selectedIndex === -1) {
                            handleAddOrSelect(selectedText);
                        }
                    },
                    loading: isLoadingAll,
                    style: {
                        width: "100%"
                    },
                    filterOption: (input, option)=>(option?.label ?? "").toLowerCase().includes(input.toLowerCase()),
                    options: availableTexts.map((text)=>({
                            value: text,
                            label: text
                        })),
                    dropdownRender: (menu)=>/*#__PURE__*/ _jsxs("div", {
                            children: [
                                menu,
                                /*#__PURE__*/ _jsx("div", {
                                    style: {
                                        padding: "8px",
                                        textAlign: "center"
                                    },
                                    children: /*#__PURE__*/ _jsx(Button, {
                                        type: "primary",
                                        icon: /*#__PURE__*/ _jsx(PlusOutlined, {}),
                                        onClick: ()=>selectedText && handleAddOrSelect(selectedText),
                                        disabled: !isNewText,
                                        children: t("Add new text")
                                    })
                                })
                            ]
                        })
                }, selectKey)
            }),
            /*#__PURE__*/ _jsx("div", {
                style: {
                    maxHeight: "300px",
                    overflowY: "auto",
                    border: "1px solid #d9d9d9",
                    borderRadius: "6px"
                },
                children: /*#__PURE__*/ _jsx(List, {
                    loading: isLoading,
                    dataSource: untranslatables,
                    size: "small",
                    renderItem: (item)=>/*#__PURE__*/ _jsx(List.Item, {
                            style: {
                                padding: "8px 12px"
                            },
                            actions: [
                                /*#__PURE__*/ _jsx(Button, {
                                    type: "text",
                                    danger: true,
                                    size: "small",
                                    icon: /*#__PURE__*/ _jsx(DeleteOutlined, {}),
                                    onClick: ()=>handleDelete(item.text)
                                }, "delete")
                            ],
                            children: /*#__PURE__*/ _jsx(List.Item.Meta, {
                                title: /*#__PURE__*/ _jsx("span", {
                                    style: {
                                        fontSize: "14px"
                                    },
                                    children: item.text
                                })
                            })
                        })
                })
            })
        ]
    });
};
export { UntranslatableManager };
